export class ProductPortraitVideoController {
  constructor(videos = []) {
    this.videos = videos;
  }

  prepare(video) {
    if (!video || video.dataset.lsPrepared === 'true') return;

    if (video.hasAttribute('src')) {
      video.dataset.lsSrc = video.getAttribute('src');
      video.removeAttribute('src');
    }

    video.querySelectorAll('source').forEach((source) => {
      if (!source.hasAttribute('src')) return;
      source.dataset.lsSrc = source.getAttribute('src');
      source.removeAttribute('src');
    });

    video.dataset.lsPrepared = 'true';
    video.load();
  }

  hydrate(video) {
    if (!video) return;

    if (video.dataset.lsSrc && !video.hasAttribute('src')) {
      video.setAttribute('src', video.dataset.lsSrc);
    }

    video.querySelectorAll('source').forEach((source) => {
      if (source.dataset.lsSrc && !source.hasAttribute('src')) source.setAttribute('src', source.dataset.lsSrc);
    });

    video.load();
  }

  async play(video) {
    if (!video) return;

    this.videos.forEach((candidate) => {
      if (candidate !== video) this.unload(candidate);
    });

    this.hydrate(video);
    try {
      await video.play();
    } catch {
      this.unload(video);
    }
  }

  unload(video) {
    if (!video) return;
    video.pause();
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
    video.load();
    const card = video.closest?.('.ls-pdp-videos__card');
    const button = card?.querySelector('[data-ls-pdp-video-toggle]');
    card?.classList.remove('is-playing');
    button?.setAttribute('aria-pressed', 'false');
    button?.setAttribute('aria-label', 'Play video');
  }

  unloadAll() {
    this.videos.forEach((video) => this.unload(video));
  }
}

export class ProductRecommendationRailController {
  constructor({ root, rail, previousButton, nextButton }) {
    this.root = root;
    this.rail = rail;
    this.previousButton = previousButton;
    this.nextButton = nextButton;
  }

  get cards() {
    return [...(this.rail?.querySelectorAll('.ls-pdp-recommendations__card') || [])];
  }

  get step() {
    const cards = this.cards;
    if (cards.length > 1) return Math.abs(cards[1].offsetLeft - cards[0].offsetLeft);
    return cards[0]?.offsetWidth || this.rail?.clientWidth || 0;
  }

  sync() {
    if (!this.rail) return;
    const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    const hasOverflow = maximum > 1;
    this.root.hidden = !hasOverflow;
    this.previousButton.disabled = !hasOverflow || this.rail.scrollLeft <= 1;
    this.nextButton.disabled = !hasOverflow || this.rail.scrollLeft >= maximum - 1;
  }

  scroll(direction, reducedMotion = false) {
    if (!this.rail || !this.step) return;
    this.rail.scrollBy({
      left: this.step * direction,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }
}

const ProductEditorialElement = globalThis.HTMLElement ?? class {};

class LSProductRecommendationControls extends ProductEditorialElement {
  connectedCallback() {
    this.rail = this.closest('.ls-pdp-recommendations__details')?.querySelector('.ls-pdp-recommendations__rail');
    this.previousButton = this.querySelector('[data-ls-recommendation-previous]');
    this.nextButton = this.querySelector('[data-ls-recommendation-next]');
    if (!this.rail || !this.previousButton || !this.nextButton) return;

    this.controller = new ProductRecommendationRailController({
      root: this,
      rail: this.rail,
      previousButton: this.previousButton,
      nextButton: this.nextButton,
    });
    this.onScroll = () => this.scheduleSync();
    this.onPrevious = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.previousButton.addEventListener('click', this.onPrevious);
    this.nextButton.addEventListener('click', this.onNext);

    if ('ResizeObserver' in globalThis) {
      this.resizeObserver = new ResizeObserver(() => this.scheduleSync());
      this.resizeObserver.observe(this.rail);
    }
    this.scheduleSync();
  }

  disconnectedCallback() {
    this.rail?.removeEventListener('scroll', this.onScroll);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.resizeObserver?.disconnect();
    if (this.syncFrame) globalThis.cancelAnimationFrame?.(this.syncFrame);
  }

  move(direction) {
    const reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.controller?.scroll(direction, reducedMotion);
  }

  scheduleSync() {
    if (this.syncFrame) return;
    this.syncFrame = globalThis.requestAnimationFrame?.(() => {
      this.syncFrame = 0;
      this.controller?.sync();
    });
    if (!this.syncFrame) this.controller?.sync();
  }
}

class LSProductVideoShowcase extends ProductEditorialElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('.ls-pdp-videos__video')];
    this.grid = this.querySelector('.ls-pdp-videos__grid');
    this.cards = [...this.querySelectorAll('.ls-pdp-videos__card')];
    this.dots = [...this.querySelectorAll('.ls-pdp-videos__dots span')];
    this.controller = new ProductPortraitVideoController(this.videos);
    this.videos.forEach((video) => this.prepareVideo(video));
    this.grid?.addEventListener('scroll', () => this.scheduleDotSync(), { passive: true });

    if ('IntersectionObserver' in globalThis) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) this.controller.unloadAll();
      }, { rootMargin: '120px 0px', threshold: 0.05 });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.unloadAll();
  }

  prepareVideo(video) {
    this.controller.prepare(video);
    const card = video.closest('.ls-pdp-videos__card');
    const button = card?.querySelector('[data-ls-pdp-video-toggle]');

    const sync = () => {
      const isPlaying = !video.paused && !video.ended;
      card?.classList.toggle('is-playing', isPlaying);
      button?.setAttribute('aria-pressed', String(isPlaying));
      if (button) button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    };

    button?.addEventListener('click', () => {
      if (video.paused) this.controller.play(video);
      else video.pause();
    });
    video.addEventListener('click', () => {
      if (video.paused) this.controller.play(video);
      else video.pause();
    });
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', sync);
  }

  scheduleDotSync() {
    if (this.dotFrame) return;
    this.dotFrame = globalThis.requestAnimationFrame?.(() => {
      this.dotFrame = 0;
      if (!this.grid || !this.cards.length) return;
      const index = this.cards.reduce((closest, card, candidateIndex) => {
        const currentDistance = Math.abs(this.cards[closest].offsetLeft - this.grid.scrollLeft);
        const candidateDistance = Math.abs(card.offsetLeft - this.grid.scrollLeft);
        return candidateDistance < currentDistance ? candidateIndex : closest;
      }, 0);
      this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    });
  }
}

class LSProductDelivery extends ProductEditorialElement {
  connectedCallback() {
    const duration = Number.parseInt(this.dataset.animationDuration, 10);
    if (Number.isFinite(duration)) this.style.setProperty('--ls-pdp-delivery-duration', `${duration}ms`);

    if (!('IntersectionObserver' in globalThis)) {
      this.classList.add('is-visible');
      return;
    }

    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', entry.isIntersecting);
    }, { threshold: 0.4 });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

}

const storeReviewFeeds = new Map();

export class StoreReviewFeed {
  constructor(fetchPage) {
    this.fetchPage = fetchPage;
    this.feeds = {
      products: { page: 0, size: 25, total: null, reviews: [], done: false, pending: null },
      shop: { page: 0, size: 10, total: null, reviews: [], done: false, pending: null },
    };
  }

  timestamp(review) {
    const value = String(review.created_at || '').replace(/ UTC$/, 'Z').replace(' ', 'T');
    return new Date(value).getTime() || 0;
  }

  unique(reviews) {
    const seen = new Set();
    return reviews.filter((review) => {
      const id = review.id || [review.reviewer_name, review.created_at, review.body_html].join('|');
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  get complete() { return Object.values(this.feeds).every((feed) => feed.done); }
  get totalCount() { return Object.values(this.feeds).reduce((sum, feed) => sum + (feed.total || 0), 0); }

  get reviews() {
    const feeds = Object.values(this.feeds);
    const boundaries = feeds.filter((feed) => feed.page && !feed.done && feed.reviews.length).map((feed) => this.timestamp(feed.reviews.at(-1)));
    const boundary = boundaries.length ? Math.max(...boundaries) : -Infinity;
    return this.unique(feeds.flatMap((feed) => feed.reviews))
      .filter((review) => this.complete || this.timestamp(review) >= boundary)
      .sort((a, b) => this.timestamp(b) - this.timestamp(a));
  }

  async load(name) {
    const feed = this.feeds[name];
    if (feed.done) return;
    if (feed.pending) return feed.pending;
    const page = feed.page + 1;
    feed.pending = (async () => {
      const result = await this.fetchPage(name, page);
      const total = result.totalCount ?? feed.total;
      if (!Array.isArray(result.reviews) || !Number.isFinite(total)) throw new Error('Invalid review page');
      feed.reviews = this.unique([...feed.reviews, ...result.reviews]);
      feed.page = page;
      feed.total = total;
      feed.done = !result.reviews.length || page * feed.size >= total;
    })();
    try { await feed.pending; } finally { feed.pending = null; }
  }

  async ensure({ target = 12, matches = () => true, onProgress = () => {}, isCurrent = () => true, maxPages = 3 } = {}) {
    const initial = Object.keys(this.feeds).filter((name) => !this.feeds[name].page);
    if (initial.length) {
      const results = await Promise.allSettled(initial.map(async (name) => { await this.load(name); onProgress(); }));
      const failure = results.find((result) => result.status === 'rejected');
      if (failure) throw failure.reason;
    }
    for (let count = 0; count < maxPages && isCurrent() && !this.complete && this.reviews.filter(matches).length < target; count += 1) {
      const next = Object.entries(this.feeds).filter(([, feed]) => !feed.done)
        .sort(([, a], [, b]) => this.timestamp(b.reviews.at(-1) || {}) - this.timestamp(a.reviews.at(-1) || {}))[0]?.[0];
      if (!next) break;
      await this.load(next);
      onProgress();
    }
    return { reviews: this.reviews, totalCount: this.totalCount, complete: this.complete };
  }
}

export class LSJudgeMePreview extends ProductEditorialElement {
  connectedCallback() {
    this.cards = this.querySelector('[data-ls-judge-cards]');
    this.heading = this.querySelector('[data-ls-judge-heading]');
    this.allLink = this.querySelector('[data-ls-judge-all]');
    this.cardTemplate = this.querySelector('[data-ls-judge-card-template]');
    this.drawer = this.querySelector('[data-ls-judge-drawer]');
    this.drawerPanel = this.querySelector('[data-ls-judge-drawer-panel]');
    this.drawerCards = this.querySelector('[data-ls-judge-drawer-cards]');
    this.drawerLoading = this.querySelector('[data-ls-judge-loading]');
    this.productCount = this.querySelector('[data-ls-judge-product-count]');
    this.errorMessage = this.querySelector('[data-ls-judge-error]');
    this.retryButton = this.querySelector('[data-ls-judge-retry]');
    this.filters = this.querySelector('[data-ls-judge-filters]');
    this.category = 'all';
    this.viewVersion = 0;
    this.storeLimits = new Map();
    this.scrollPositions = new Map();
    this.moreButton = this.querySelector('[data-ls-judge-more]');
    this.skeleton = this.querySelector('[data-ls-judge-skeleton]');
    if (!storeReviewFeeds.has(this.dataset.shopDomain)) {
      storeReviewFeeds.set(this.dataset.shopDomain, new StoreReviewFeed((name, page) => name === 'products' ? this.fetchStoreReviewPage(page) : this.fetchReviewPage(page, '0')));
    }
    this.storeFeed = storeReviewFeeds.get(this.dataset.shopDomain);
    this.moreButton?.addEventListener('click', () => this.loadMoreReviews());
    this.onDrawerScroll = () => {
      const top = this.drawerPanel.scrollTop;
      const movingDown = top > (this.lastPanelScroll || 0);
      this.lastPanelScroll = top;
      if (movingDown && this.activeTab === 'store' && !this.storeViewLoading && !this.moreButton?.hidden && this.drawerPanel.scrollHeight - top - this.drawerPanel.clientHeight < 180) void this.loadMoreReviews();
    };
    this.drawerPanel?.addEventListener('scroll', this.onDrawerScroll, { passive: true });
    this.catalog = new Map();
    try {
      const products = JSON.parse(this.querySelector('[data-ls-judge-catalog]')?.textContent || '[]');
      products.forEach((product) => this.catalog.set(product.url, product));
    } catch { /* Product links remain usable without catalog enrichment. */ }
    if (this.filters && this.drawerPanel) this.drawerPanel.insertBefore(this.filters, this.drawerCards);
    this.querySelectorAll('[data-ls-judge-category]').forEach((button) => {
      button.addEventListener('click', () => {
        this.saveReviewScroll();
        this.category = button.dataset.lsJudgeCategory;
        this.querySelectorAll('[data-ls-judge-category]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        this.renderDrawerReviews(this.storeFeed.reviews);
        this.restoreReviewScroll();
        void this.loadDrawerTab('store');
      });
    });
    this.retryButton?.addEventListener('click', () => this.loadDrawerTab(this.activeTab));
    this.tabButtons = [...this.querySelectorAll('[data-ls-judge-tab]')];
    this.buttonLabel = (this.allLink?.textContent.trim() || 'See all').replace(/\s+reviews?$/i, '');
    this.allLink?.addEventListener('click', (event) => this.openReviews(event));
    this.querySelectorAll('[data-ls-judge-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });
    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => this.activateTab(button.dataset.lsJudgeTab));
    });
    this.onKeydown = (event) => this.handleKeydown(event);
    document.addEventListener('keydown', this.onKeydown);

    if (this.hydrate()) return;

    this.observer = new MutationObserver(() => {
      if (!this.hydrate()) return;
      this.observer?.disconnect();
    });
    this.observer.observe(this, { childList: true, subtree: true, attributes: true });
    this.timeout = globalThis.setTimeout?.(() => this.observer?.disconnect(), 12000);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.timeout) globalThis.clearTimeout?.(this.timeout);
    document.removeEventListener('keydown', this.onKeydown);
    this.viewVersion += 1;
    this.drawerPanel?.removeEventListener('scroll', this.onDrawerScroll);
    if (this.drawer) this.drawer.hidden = true;
    syncProductPageScrollLock();
  }

  hydrate() {
    const widget = this.querySelector('.ls-pdp-judge__source .jdgm-rev-widg');
    if (!widget || !this.cards || !this.cardTemplate) return false;

    const reviews = this.reviewsFromWidget(widget);
    if (!Array.isArray(reviews) || !reviews.length) return false;

    const rating = widget.dataset.averageRating || this.dataset.fallbackRating;
    const count = Number.parseInt(widget.dataset.numberOfReviews || this.dataset.fallbackCount, 10);
    if (!rating || !Number.isFinite(count) || count < 1) return false;

    const spanish = document.documentElement.lang.toLowerCase().startsWith('es');
    const ratedLabel = spanish ? 'Valoración' : 'Rated';
    const reviewLabel = spanish ? (count === 1 ? 'opinión' : 'opiniones') : (count === 1 ? 'review' : 'reviews');
    this.heading.textContent = `${this.dataset.heading || (spanish ? 'Opiniones' : 'Reviews')} — ${ratedLabel} ${rating}`;
    this.allLink.textContent = `${this.buttonLabel} ${count} ${reviewLabel}`;
    if (this.productCount) this.productCount.textContent = String(count);
    this.reviewCount = count;
    this.reviews = reviews;
    this.productReviews = reviews;
    this.renderReviews(reviews.slice(0, 2));
    this.hidden = false;
    return true;
  }

  reviewsFromWidget(widget) {
    const renderedReviews = [...widget.querySelectorAll('.jdgm-rev')]
      .map((review) => ({
        id: review.dataset.reviewId,
        rating: review.querySelector('.jdgm-rev__rating')?.dataset.score,
        created_at:
          review.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') ||
          review.querySelector('.jdgm-rev__timestamp')?.dataset.content,
        title: review.querySelector('.jdgm-rev__title')?.textContent.trim() || '',
        body_html: review.querySelector('.jdgm-rev__body')?.innerHTML || '',
        reviewer_name: review.querySelector('.jdgm-rev__author')?.textContent.trim() || '',
        verified_buyer: review.dataset.verifiedBuyer === 'true',
        product_url: review.querySelector('.jdgm-rev__prod-link')?.getAttribute('href') || review.dataset.productUrl || '',
        product_title: review.querySelector('.jdgm-rev__prod-link')?.textContent.trim() || review.dataset.productTitle || '',
        photos: [...review.querySelectorAll('.jdgm-rev__pic-link')].map((link) => ({
          url: link.getAttribute('data-mfp-src') || link.getAttribute('href'),
          alt: link.querySelector('img')?.getAttribute('alt') || '',
        })),
      }))
      .filter((review) => (review.body_html || review.title) && Number(review.rating) >= 1 && Number(review.rating) <= 5);
    if (renderedReviews.length) return renderedReviews;

    const reviewData = widget.querySelector('.jdgm-gallery-data[data-json]');
    if (!reviewData?.dataset.json) return [];
    try {
      const reviews = JSON.parse(reviewData.dataset.json);
      return Array.isArray(reviews) ? reviews.map((review) => ({ ...review, id: review.id || review.uuid })) : [];
    } catch {
      return [];
    }
  }

  renderReviews(reviews) {
    this.cards.replaceChildren();
    reviews.forEach((review) => this.cards.append(this.renderCard(review)));
  }

  viewKey() { return `${this.activeTab}:${this.activeTab === 'store' ? this.category : ''}`; }

  saveReviewScroll() { this.scrollPositions?.set(this.viewKey(), this.drawerPanel?.scrollTop || 0); }
  restoreReviewScroll() {
    if (this.drawerPanel) this.drawerPanel.scrollTop = this.scrollPositions?.get(this.viewKey()) || 0;
    this.lastPanelScroll = this.drawerPanel?.scrollTop || 0;
  }

  matchesCategory(review) {
    return this.category === 'all' || Boolean(this.catalog.get(this.productPath(review.product_url))?.collections.includes(this.category));
  }

  renderDrawerReviews(reviews) {
    if (!this.drawerCards) return;
    const filtered = this.activeTab === 'store' ? reviews.filter((review) => this.matchesCategory(review)) : reviews;
    const selected = this.activeTab === 'store' ? filtered.slice(0, this.storeLimits.get(this.category) || 12) : filtered;
    const key = this.viewKey();
    const ids = selected.map((review) => review.id || `${review.reviewer_name}|${review.created_at}|${review.body_html}`);
    const oldIds = this.renderedReviewIds || [];
    const append = this.renderedReviewKey === key && oldIds.length <= ids.length && oldIds.every((id, index) => id === ids[index]);
    if (!append) this.drawerCards.replaceChildren();
    selected.slice(append ? oldIds.length : 0).forEach((review) => this.drawerCards.append(this.renderCard(review, this.activeTab === 'store', true)));
    this.renderedReviewKey = key;
    this.renderedReviewIds = ids;
    this.drawerCards.dataset.visibleCount = String(selected.length);
    this.moreAvailable = this.activeTab === 'store' && (!this.storeFeed.complete || filtered.length > selected.length);
    if (this.moreButton) this.moreButton.hidden = !this.moreAvailable;
    if (this.skeleton) this.skeleton.hidden = selected.length > 0 || !this.storeViewLoading;
  }

  async loadMoreReviews() {
    if (this.activeTab !== 'store' || this.storeViewLoading || !this.moreAvailable) return;
    this.storeLimits.set(this.category, (this.storeLimits.get(this.category) || 12) + 12);
    this.renderDrawerReviews(this.storeFeed.reviews);
    await this.loadDrawerTab('store');
  }

  async openReviews(event) {
    event.preventDefault();
    if (!this.reviews?.length) return;

    if (!this.drawer || !this.drawerPanel) return;
    this.returnFocus = event.currentTarget;
    this.drawer.hidden = false;
    syncProductPageScrollLock();
    this.setActiveTab(this.activeTab || 'product');
    this.renderDrawerReviews(this.activeTab === 'store' ? this.storeFeed.reviews : this.productReviews || this.reviews);
    this.restoreReviewScroll();
    this.drawerPanel.focus({ preventScroll: true });
    await this.loadDrawerTab(this.activeTab);
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.saveReviewScroll();
    this.viewVersion += 1;
    this.storeViewLoading = false;
    this.drawer.hidden = true;
    syncProductPageScrollLock();
    this.returnFocus?.focus({ preventScroll: true });
  }

  handleKeydown(event) {
    if (!this.drawer || this.drawer.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    if (event.target?.matches('[data-ls-judge-tab]') && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const next = event.key === 'Home' ? this.tabButtons[0] : event.key === 'End' ? this.tabButtons[1] : this.tabButtons.find((button) => button !== event.target);
      next?.focus();
      if (next) void this.activateTab(next.dataset.lsJudgeTab);
      return;
    }
    if (event.key !== 'Tab' || !this.drawerPanel) return;
    const focusable = [...this.drawerPanel.querySelectorAll('button:not([disabled]), a[href]')].filter(
      (element) => !element.closest('[hidden]') && element.tabIndex >= 0
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async activateTab(tabName) {
    if (tabName !== 'product' && tabName !== 'store') return;
    this.saveReviewScroll();
    this.setActiveTab(tabName);
    const cachedReviews = tabName === 'store' ? this.storeFeed.reviews : this.productReviews;
    this.renderDrawerReviews(cachedReviews || []);
    this.restoreReviewScroll();
    await this.loadDrawerTab(tabName);
  }

  setActiveTab(tabName) {
    this.activeTab = tabName;
    if (this.errorMessage) this.errorMessage.hidden = true;
    if (this.retryButton) this.retryButton.hidden = true;
    if (this.filters) this.filters.hidden = tabName !== 'store';
    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.lsJudgeTab === tabName;
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });
  }

  async loadDrawerTab(tabName) {
    const token = ++this.viewVersion;
    const category = this.category;
    const current = () => token === this.viewVersion && this.activeTab === tabName && !this.drawer.hidden;
    this.storeViewLoading = tabName === 'store';
    if (this.errorMessage) this.errorMessage.hidden = true;
    if (this.retryButton) this.retryButton.hidden = true;
    if (this.drawerLoading) this.drawerLoading.hidden = false;
    if (this.moreButton) this.moreButton.disabled = true;
    this.drawerCards?.setAttribute('aria-busy', 'true');
    if (tabName === 'store') this.renderDrawerReviews(this.storeFeed.reviews);
    try {
      if (tabName === 'store') {
        const progress = () => {
          if (!current()) return;
          this.renderDrawerReviews(this.storeFeed.reviews);
        };
        await this.storeFeed.ensure({ target: this.storeLimits.get(category) || 12, matches: (review) => category === 'all' || Boolean(this.catalog.get(this.productPath(review.product_url))?.collections.includes(category)), isCurrent: current, onProgress: progress });
        if (current()) {
          progress();
          if (!this.drawerCards.children.length && this.storeFeed.complete && this.errorMessage) {
            this.errorMessage.textContent = this.dataset.emptyFilterLabel;
            this.errorMessage.hidden = false;
          }
        }
      } else {
        if (!this.productReviews?.length || this.productReviews.length < this.reviewCount) {
          this.productPromise ||= this.loadReviewSet(this.dataset.productId).then((result) => {
            this.productReviews = result.reviews;
            this.reviewCount = result.totalCount;
          }).finally(() => { this.productPromise = null; });
          await this.productPromise;
        }
        if (current()) this.renderDrawerReviews(this.productReviews || this.reviews);
      }
    } catch {
      if (current()) {
        this.renderDrawerReviews(tabName === 'store' ? this.storeFeed.reviews : this.productReviews || this.reviews);
        if (this.errorMessage) { this.errorMessage.textContent = this.dataset.errorLabel; this.errorMessage.hidden = false; }
        if (this.retryButton) this.retryButton.hidden = false;
      }
    } finally {
      if (current()) {
        this.storeViewLoading = false;
        if (this.drawerLoading) this.drawerLoading.hidden = true;
        if (this.skeleton) this.skeleton.hidden = true;
        if (this.moreButton) this.moreButton.disabled = false;
        this.drawerCards?.removeAttribute('aria-busy');
      }
    }
  }

  async loadReviewSet(productId) {
    const first = await this.fetchReviewPage(1, productId);
    let reviews = [...first.reviews];
    for (let page = 2; page <= Math.ceil(first.totalCount / 10); page += 3) {
      const batch = Array.from({ length: Math.min(3, Math.ceil(first.totalCount / 10) - page + 1) }, (_, index) => page + index);
      const results = await Promise.all(batch.map((number) => this.fetchReviewPage(number, productId)));
      reviews.push(...results.flatMap((result) => result.reviews));
    }
    return { reviews: this.dedupeReviews(reviews), totalCount: first.totalCount };
  }

  async fetchStoreReviewPage(page) {
    const params = new URLSearchParams({ shop_domain: this.dataset.shopDomain, platform: 'shopify', review_type: 'product-reviews', page: String(page), per_page: '25' });
    const payload = await this.reviewJson(`https://cdn.judge.me/reviews/all_reviews_js_based?${params}`);
    const parsed = new DOMParser().parseFromString(payload.html || '', 'text/html');
    const total = Number.parseInt(payload.number_of_product_reviews, 10);
    return { reviews: this.reviewsFromWidget(parsed), totalCount: Number.isFinite(total) ? total : null };
  }

  async fetchReviewPage(page, productId) {
    const params = new URLSearchParams({
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      product_id: productId,
      page: String(page),
      per_page: '10',
    });
    const payload = await this.reviewJson(`https://api.judge.me/reviews/reviews_for_widget?${params}`);
    const document = new DOMParser().parseFromString(payload.html || '', 'text/html');
    const total = Number.parseInt(payload.total_count, 10);
    return {
      reviews: this.reviewsFromWidget(document),
      totalCount: Number.isFinite(total) ? total : null,
    };
  }

  async reviewJson(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Review request failed');
      const payload = await response.json();
      if (typeof payload.html !== 'string') throw new Error('Invalid review response');
      return payload;
    } finally {
      clearTimeout(timer);
    }
  }

  dedupeReviews(reviews) {
    const seen = new Set();
    return reviews.filter((review) => {
      const key =
        review.id ||
        [review.reviewer_name, review.created_at, this.plainText(review.body_html || '')].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  productPath(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return '';
      return url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/products\/)/i, '');
    } catch { return ''; }
  }

  renderCard(review, showProduct = false, showMedia = false) {
    const fragment = this.cardTemplate.content.cloneNode(true);
    const rating = Math.max(1, Math.min(5, Number.parseInt(review.rating, 10) || 5));
    const body = this.plainText(review.body_html || '');
    const createdAt = new Date(review.created_at);
    const stars = fragment.querySelector('[data-ls-judge-stars]');
    stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${rating} / 5`);
    const productLink = fragment.querySelector('[data-ls-judge-product]');
    const productPath = this.productPath(review.product_url);
    if (productLink && showProduct && productPath) {
      productLink.hidden = false;
      productLink.href = productPath;
      fragment.querySelector('[data-ls-judge-product-title]').textContent = review.product_title;
      const image = fragment.querySelector('[data-ls-judge-product-image]');
      const productImage = this.catalog.get(productPath)?.image;
      if (productImage) image.src = productImage;
      else image.remove();
    } else productLink?.remove();
    fragment.querySelector('[data-ls-judge-date]').textContent = Number.isNaN(createdAt.getTime())
      ? ''
      : createdAt.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        });
    const title = fragment.querySelector('[data-ls-judge-title]');
    if (review.title) title.textContent = review.title;
    else title.remove();
    const media = fragment.querySelector('[data-ls-judge-media]');
    if (showMedia && media) {
      (review.photos || []).forEach((photo) => {
        const src = this.reviewPhotoUrl(photo.url);
        if (!src) return;
        const link = document.createElement('a');
        link.href = src; link.target = '_blank'; link.rel = 'noopener';
        const image = document.createElement('img');
        image.src = src; image.alt = photo.alt || this.dataset.photoLabel || 'Customer photo';
        image.loading = 'lazy'; image.decoding = 'async';
        link.append(image); media.append(link);
      });
      if (!media.children.length) media.remove();
    } else media?.remove();
    fragment.querySelector('[data-ls-judge-body]').textContent = body;
    fragment.querySelector('[data-ls-judge-author]').textContent = review.reviewer_name || 'Customer';
    const verified = fragment.querySelector('[data-ls-judge-verified]');
    if (review.verified_buyer) verified.textContent = this.dataset.verifiedLabel || 'Verified buyer';
    else verified.remove();
    return fragment;
  }

  reviewPhotoUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || !['review-images.judgeme.com', 'cdn.judge.me', 'cdn.shopify.com', window.location.hostname].includes(url.hostname)) return '';
      if (url.hostname === 'review-images.judgeme.com') { url.searchParams.set('width', '640'); url.searchParams.set('quality', '80'); }
      return url.href;
    } catch { return ''; }
  }

  plainText(html) {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent.trim();
  }
}

export function syncProductPageScrollLock(root = globalThis.document) {
  if (!root) return false;

  const isLocked = Boolean(
    root.querySelector(
      'product-info .product__accordion details[open] > .ls-pdp-drawer, product-info .ls-pdp-variant-drawer[open], product-info [data-ls-judge-drawer]:not([hidden])'
    )
  );
  root.documentElement?.classList.toggle('ls-pdp-drawer-open', isLocked);
  root.body?.classList.toggle('ls-pdp-drawer-open', isLocked);
  return isLocked;
}

export class ProductVariantMediaNavigator {
  constructor(root = document) {
    this.root = root;
    this.mediaQuery = globalThis.matchMedia?.('(min-width: 990px)');
    root.querySelectorAll('product-info').forEach((productInfo) => this.prepare(productInfo));
  }

  prepare(productInfo) {
    if (productInfo.dataset.lsVariantNavigatorReady === 'true') return;
    productInfo.dataset.lsVariantNavigatorReady = 'true';
    this.enhanceSecondaryOptions(productInfo);
    this.mediaQuery?.addEventListener?.('change', (event) => {
      if (!event.matches) {
        productInfo.querySelectorAll('.ls-pdp-variant-drawer[open]').forEach((drawer) => drawer.close());
        syncProductPageScrollLock();
        return;
      }
      this.enhanceSecondaryOptions(productInfo);
    });
    const observer = new MutationObserver(() => this.enhanceSecondaryOptions(productInfo));
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  enhanceSecondaryOptions(productInfo) {
    if (!this.mediaQuery?.matches) return;
    productInfo
      .querySelectorAll('variant-selects fieldset[data-ls-option-position]:not([data-ls-option-position="1"])')
      .forEach((fieldset) => {
        if (fieldset.querySelector('.ls-pdp-desktop-variant-trigger')) return;
        const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
        if (!inputs.length) return;

        const optionName = inputs[0].dataset.optionName || 'Product option';
        const optionPosition = fieldset.dataset.lsOptionPosition || 'option';
        const sectionId = productInfo.dataset.section || productInfo.id || 'product';
        const drawerId = `LSPDPVariantDrawer-${sectionId}-${optionPosition}`;
        const titleId = `${drawerId}-Title`;
        const selectedInput = inputs.find((input) => input.checked) || inputs[0];

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'ls-pdp-desktop-variant-trigger';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', drawerId);

        const triggerValue = document.createElement('span');
        triggerValue.className = 'ls-pdp-desktop-variant-trigger__value';
        triggerValue.textContent = selectedInput.value;
        trigger.append(triggerValue);

        const triggerCaret = document.createElement('span');
        triggerCaret.className = 'ls-pdp-desktop-variant-trigger__caret';
        triggerCaret.setAttribute('aria-hidden', 'true');
        trigger.append(triggerCaret);

        const drawer = document.createElement('dialog');
        drawer.id = drawerId;
        drawer.className = 'ls-pdp-variant-drawer';
        drawer.setAttribute('aria-labelledby', titleId);

        const header = document.createElement('div');
        header.className = 'ls-pdp-variant-drawer__header';

        const title = document.createElement('h2');
        title.id = titleId;
        title.className = 'ls-pdp-variant-drawer__title';
        title.textContent = `Select ${optionName}`;
        header.append(title);

        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'ls-pdp-variant-drawer__close';
        closeButton.setAttribute('aria-label', `Close ${optionName} options`);
        header.append(closeButton);
        drawer.append(header);

        const optionList = document.createElement('div');
        optionList.className = 'ls-pdp-variant-drawer__options';
        optionList.setAttribute('role', 'listbox');
        optionList.setAttribute('aria-labelledby', titleId);

        inputs.forEach((input) => {
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'ls-pdp-variant-drawer__option';
          optionButton.setAttribute('role', 'option');
          optionButton.setAttribute('aria-selected', String(input.checked));
          if (input.disabled || input.classList.contains('disabled')) {
            optionButton.classList.add('is-unavailable');
          }

          const optionLabel = document.createElement('span');
          optionLabel.textContent = input.value;
          optionButton.append(optionLabel);

          const check = document.createElement('span');
          check.className = 'ls-pdp-variant-drawer__check';
          check.setAttribute('aria-hidden', 'true');
          optionButton.append(check);

          optionButton.addEventListener('click', () => {
            this.closeVariantDrawer(drawer, trigger);
            if (input.checked) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          optionList.append(optionButton);
        });

        optionList.addEventListener('keydown', (event) => {
          const options = [...optionList.querySelectorAll('[role="option"]')];
          const current = options.indexOf(document.activeElement);
          let next = current;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % options.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = options.length - 1;
          if (next === current || next < 0) return;
          event.preventDefault();
          options[next]?.focus();
        });

        drawer.append(optionList);
        closeButton.addEventListener('click', () => this.closeVariantDrawer(drawer, trigger));
        drawer.addEventListener('click', (event) => {
          if (event.target === drawer) this.closeVariantDrawer(drawer, trigger);
        });
        drawer.addEventListener('close', () => {
          trigger.setAttribute('aria-expanded', 'false');
          syncProductPageScrollLock();
          if (trigger.isConnected) trigger.focus({ preventScroll: true });
        });
        trigger.addEventListener('click', () => {
          document.querySelectorAll('.ls-pdp-variant-drawer[open]').forEach((openDrawer) => {
            if (openDrawer !== drawer) openDrawer.close();
          });
          trigger.setAttribute('aria-expanded', 'true');
          drawer.showModal();
          syncProductPageScrollLock();
          optionList.querySelector('[aria-selected="true"]')?.focus({ preventScroll: true });
        });

        fieldset.classList.add('is-ls-desktop-drawer');
        fieldset.append(trigger, drawer);
      });
  }

  closeVariantDrawer(drawer, trigger) {
    if (!drawer.open) return;
    trigger.setAttribute('aria-expanded', 'false');
    drawer.close();
    syncProductPageScrollLock();
  }
}

export class ProductRatingJumpController {
  constructor(root = document) {
    root.querySelectorAll('[data-ls-rating-jump]').forEach((link) => this.prepare(link));
  }

  prepare(link) {
    if (link.dataset.lsRatingJumpReady === 'true') return;
    link.dataset.lsRatingJumpReady = 'true';
    const productReviewAnchor = link.closest('product-info')?.querySelector('.ls-pdp-review-anchor');
    link.setAttribute('href', productReviewAnchor?.id ? `#${productReviewAnchor.id}` : '#ProductPageReviews');
    link.addEventListener('click', (event) => this.jump(link, event));
  }

  jump(link, event) {
    const selector = link.getAttribute('href');
    const target = selector ? document.querySelector(selector) : null;
    if (!target) return;

    event.preventDefault();
    const candidate = target.matches?.('ls-judgeme-preview') ? target : target.nextElementSibling;
    const reviewPreview = candidate?.matches?.('ls-judgeme-preview') ? candidate : null;
    reviewPreview?.querySelector('details')?.setAttribute('open', '');

    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    globalThis.setTimeout?.(() => target.focus({ preventScroll: true }), reduceMotion ? 0 : 420);
  }
}

export class ProductDrawerController {
  constructor(root = document) {
    this.root = root;
    this.details = [...root.querySelectorAll('product-info .product__accordion details')];
    this.details.forEach((details) => this.prepare(details));
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.closeOpenDrawer();
    };
    if (root === document) document.addEventListener('keydown', this.onKeydown);
  }

  prepare(details) {
    if (details.dataset.lsDrawerReady === 'true') return;
    details.dataset.lsDrawerReady = 'true';
    details.addEventListener('toggle', () => {
      if (!details.open) {
        syncProductPageScrollLock();
        return;
      }

      this.details.forEach((candidate) => {
        if (candidate !== details) candidate.removeAttribute('open');
      });
      syncProductPageScrollLock();
      details.querySelector('.ls-pdp-drawer__panel')?.focus({ preventScroll: true });
    });
    details.querySelectorAll('[data-ls-pdp-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(details));
    });
  }

  close(details) {
    const summary = details.querySelector('summary');
    details.removeAttribute('open');
    syncProductPageScrollLock();
    summary?.focus({ preventScroll: true });
  }

  closeOpenDrawer() {
    const openDetails = document.querySelector('product-info .product__accordion details[open]');
    if (openDetails) this.close(openDetails);
  }
}

export function selectProductDesktopBalanceTargets({
  leftHeight = 0,
  rightHeight = 0,
  candidateGains = [],
  minimumDeficit = 320,
  targetGap = 180,
  maximumOvershoot = 240,
} = {}) {
  let remainingGap = Number(leftHeight) - Number(rightHeight);
  if (!Number.isFinite(remainingGap) || remainingGap < minimumDeficit) return [];

  const targets = [];
  candidateGains.forEach((candidateGain, index) => {
    if (remainingGap <= targetGap) return;

    const gain = Number(candidateGain);
    if (!Number.isFinite(gain) || gain <= 0) return;

    const nextGap = remainingGap - gain;
    if (nextGap < -maximumOvershoot) return;

    targets.push(index);
    remainingGap = nextGap;
  });

  return targets;
}

export class ProductDesktopBalanceController {
  constructor(root = document) {
    this.root = root;
    this.mediaQuery = globalThis.matchMedia?.('(min-width: 990px)');
    root.querySelectorAll('product-info').forEach((productInfo) => this.prepare(productInfo));
  }

  prepare(productInfo) {
    if (!productInfo || productInfo.dataset.lsDesktopBalanceReady === 'true') return;

    const mediaWrapper = productInfo.querySelector('.product__media-wrapper');
    const infoWrapper = productInfo.querySelector('.product__info-wrapper');
    if (!mediaWrapper || !infoWrapper) return;

    productInfo.dataset.lsDesktopBalanceReady = 'true';
    const context = {
      productInfo,
      mediaWrapper,
      infoWrapper,
      candidates: [],
      timer: 0,
    };

    context.refreshCandidates = () => this.refreshCandidates(context);
    context.schedule = () => this.schedule(context);
    context.onBreakpointChange = () => context.schedule();

    this.refreshCandidates(context);

    if ('ResizeObserver' in globalThis) {
      context.resizeObserver = new ResizeObserver(() => context.schedule());
      context.resizeObserver.observe(mediaWrapper);
    }

    if ('MutationObserver' in globalThis) {
      context.mutationObserver = new MutationObserver(() => {
        context.refreshCandidates();
        context.schedule();
      });
      context.mutationObserver.observe(productInfo, { childList: true, subtree: true });
    }

    this.mediaQuery?.addEventListener?.('change', context.onBreakpointChange);
    context.schedule();
    globalThis.addEventListener?.('load', context.schedule, { once: true });
  }

  refreshCandidates(context) {
    context.candidates = [...context.productInfo.querySelectorAll('[data-ls-desktop-balance]')];
    context.candidates.forEach((details) => {
      if (details.dataset.lsDesktopBalancePrepared === 'true') return;

      details.dataset.lsDesktopBalancePrepared = 'true';
      details.dataset.lsDesktopBalanceName = details.getAttribute('name') || '';
      const markManual = () => {
        details.dataset.lsDesktopBalanceManual = 'true';
        details.removeAttribute('data-ls-desktop-balance-auto-opened');
        globalThis.setTimeout?.(context.schedule, 0);
      };
      details.querySelector('summary')?.addEventListener('click', markManual);
    });
  }

  schedule(context) {
    globalThis.clearTimeout?.(context.timer);
    context.timer = globalThis.setTimeout?.(() => this.balance(context), 90);
    if (!context.timer) this.balance(context);
  }

  restoreMobileGrouping(context) {
    context.candidates.forEach((details) => {
      if (details.dataset.lsDesktopBalanceAutoOpened === 'true') details.open = false;
      details.removeAttribute('data-ls-desktop-balance-auto-opened');

      const originalName = details.dataset.lsDesktopBalanceName;
      if (originalName) details.setAttribute('name', originalName);
    });
  }

  measureCandidateGain(context, details, baseHeight) {
    details.open = true;
    const expandedHeight = context.infoWrapper.getBoundingClientRect().height;
    details.open = false;
    return Math.max(0, expandedHeight - baseHeight);
  }

  balance(context) {
    if (!context.productInfo.isConnected) {
      context.resizeObserver?.disconnect();
      context.mutationObserver?.disconnect();
      this.mediaQuery?.removeEventListener?.('change', context.onBreakpointChange);
      return;
    }

    this.refreshCandidates(context);
    if (!this.mediaQuery?.matches) {
      this.restoreMobileGrouping(context);
      return;
    }

    context.candidates.forEach((details) => {
      details.removeAttribute('name');
      if (details.dataset.lsDesktopBalanceAutoOpened === 'true') details.open = false;
      details.removeAttribute('data-ls-desktop-balance-auto-opened');
    });

    const leftHeight = context.mediaWrapper.getBoundingClientRect().height;
    const rightHeight = context.infoWrapper.getBoundingClientRect().height;
    const availableCandidates = context.candidates.filter(
      (details) => details.dataset.lsDesktopBalanceManual !== 'true' && !details.open,
    );
    const candidateGains = availableCandidates.map((details) =>
      this.measureCandidateGain(context, details, rightHeight),
    );
    const targets = selectProductDesktopBalanceTargets({ leftHeight, rightHeight, candidateGains });

    targets.forEach((targetIndex) => {
      const details = availableCandidates[targetIndex];
      if (!details) return;
      details.open = true;
      details.dataset.lsDesktopBalanceAutoOpened = 'true';
    });
  }
}

if (globalThis.customElements && !globalThis.customElements.get('ls-product-video-showcase')) {
  globalThis.customElements.define('ls-product-video-showcase', LSProductVideoShowcase);
}

if (globalThis.customElements && !globalThis.customElements.get('ls-product-delivery')) {
  globalThis.customElements.define('ls-product-delivery', LSProductDelivery);
}

if (globalThis.customElements && !globalThis.customElements.get('ls-judgeme-preview')) {
  globalThis.customElements.define('ls-judgeme-preview', LSJudgeMePreview);
}

if (globalThis.customElements && !globalThis.customElements.get('ls-product-recommendation-controls')) {
  globalThis.customElements.define('ls-product-recommendation-controls', LSProductRecommendationControls);
}

if (typeof document !== 'undefined') {
  new ProductVariantMediaNavigator(document);
  new ProductRatingJumpController(document);
  new ProductDrawerController(document);
  new ProductDesktopBalanceController(document);
  document.addEventListener('shopify:section:load', (event) => {
    new ProductVariantMediaNavigator(event.target);
    new ProductRatingJumpController(event.target);
    new ProductDrawerController(event.target);
    new ProductDesktopBalanceController(event.target);
  });
}
